<template>
  <div class="xai-free-trial-landing">
    <GdprCookieBanner v-if="isGdprBannerVisible"
                      @cookie-accept="acceptCookieConsent" />
    <div v-if="!isOtcStep" class="xai-free-trial__logo-header">
      <button class="xai-free-trial__back-btn"
              aria-label="Go back"
              @click="goBack">
        <i class="wkce-icon-chevron-left" aria-hidden="true" />
      </button>
      <img :src="aiSparkIcon"
           class="xai-free-trial__spark-icon"
           aria-hidden="true"
           alt="">
      <span class="xai-free-trial__logo-text">
        <strong>UpToDate<sup>&reg;</sup></strong> Expert AI
      </span>
    </div>
    <div class="xai-free-trial__content" :class="{ 'xai-free-trial__content--otp': isOtcStep }">
      <div v-if="!isOtcStep" class="xai-free-trial__header">
        <h1 class="xai-free-trial__title ds1-utd-h2">
          Activate your free trial
        </h1>
        <p class="xai-free-trial__subtitle">
          Verify your clinician status to try Expert AI for free for 7 days
        </p>
      </div>
      <XaiFreeTrialForm @otp-step-start="isOtcStep = true" />
    </div>
  </div>
</template>

<script setup>
import { onMounted, onUnmounted, onBeforeUnmount, ref, computed } from 'vue';
import { useStore } from 'vuex';
import { SET_SHOULD_HIDE_HEADER_ITEMS } from '_acaSrc/store/feature.store';
import { SET_IS_XAI_MOBILE } from '_acaSrc/store/device.store';
import XaiFreeTrialForm from './XaiFreeTrialForm.vue';
import GdprCookieBanner from '_acaSrc/components/header/GdprCookieBanner.vue';
import { getCookie } from '_acaSrc/utility/Cookies';
import IndexManager from '_acaSrc/utility/IndexManager';
import UtdCache from '_acaSrc/utility/UtdCache';
import aiSparkIcon from '~_acaAssets/utd-ai/utd-ai-spark.svg';

const store = useStore();

const isOtcStep = ref(false);

const goBack = () => window.history.back();

const shouldCloseGdprBanner = ref(false);
const acceptCookieConsent = () => {
    shouldCloseGdprBanner.value = true;
};
const isAcceptCookieConsent = computed(() => getCookie('cookie-accept') === 't');
const isGdprBannerVisible = computed(() =>
    store.getters['feature/isShowGdprBanner']
    && !isAcceptCookieConsent.value
    && !shouldCloseGdprBanner.value
);

onMounted(() => {
    store.commit(`feature/${SET_SHOULD_HIDE_HEADER_ITEMS}`, true);
    store.commit(`device/${SET_IS_XAI_MOBILE}`, true);
    new UtdCache().setPersistent('isXAIMobile', true, 1);
    new IndexManager().bodyCss.setOrClear('xAIMobile', true);
});

onBeforeUnmount(() => {
    new IndexManager().bodyCss.clear('xAIMobile');
    store.commit(`device/${SET_IS_XAI_MOBILE}`, false);
});

onUnmounted(() => {
    store.commit(`feature/${SET_SHOULD_HIDE_HEADER_ITEMS}`, false);
});
</script>

<style lang="less">
@import (reference) '~_acaAssets/wkce/colors/wkce-app-styles';

html:has(body.xAIMobile.isTablet),
body.xAIMobile,
body.xAIMobile #appContainer,
body.xAIMobile #utd-main {
    height: 100%;
}
</style>

<style lang="less" scoped>
@import (reference) '~_acaAssets/wkce/colors/wkce-app-styles';

.xai-free-trial-landing {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100%;
  min-height: 100vh;
  height: 100%;
  margin: 0 auto;
  background-color: @WKCE-WHITE;
  box-sizing: border-box;
  overflow: hidden;
}

.xai-free-trial__logo-header {
  background-color: @WKCE-WHITE;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 8px;
  flex: 0 0 64px;
  position: relative;
  width: 100%;
  height: 64px;
  padding: 0 16px;
  box-sizing: border-box;
  align-self: stretch;
}

.xai-free-trial__back-btn {
  position: absolute;
  left: 16px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: @DS1-UTD-TOPIC-TEXT-COLOR;
  font-size: 32px;
  line-height: 1;
  width: 32px;
  height: 32px;
}

.xai-free-trial__spark-icon {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
}

.xai-free-trial__logo-text {
  font-size: 16px;
  font-weight: 500;
  color: @DS1-UTD-TOPIC-TEXT-COLOR;
  white-space: nowrap;
}

.xai-free-trial__content {
  display: flex;
  flex-direction: column;
  background-color: @WKCE-CREAM-TINT2;
  border-radius: 16px 16px 0 0;
  padding: 24px 16px;
  flex: 1 0 auto;
}

.xai-free-trial__header {
  max-width: 400px;
}

.xai-free-trial__title {
  .ds1-mt-0();
  width: 100%;
  height: auto;
  font-family: "Noto Sans", sans-serif;
  font-size: 24px;
  font-style: normal;
  font-weight: 700;
  line-height: 30px;
  letter-spacing: 0;
  margin-bottom: 8px;
  color: @WKCE-GRAY;
  font-feature-settings: 'liga' off, 'clig' off;
}

.xai-free-trial__subtitle {
  .ds1-mt-0();
  width: 100%;
  height: auto;
  font-family: "Noto Sans", sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 21px;
  letter-spacing: 0;
  margin-bottom: 8px;
  color: @WKCE-GRAY;
  font-feature-settings: 'liga' off, 'clig' off;
}

.xai-free-trial__content--otp {
  background-color: @WKCE-WHITE;
  border-radius: 0;
  padding: 0;
}
</style>
